import ConfirmationDot from '@/components/ui/verification/confirmation-dot.tsx';
import HoverClickStay from '@/components/ux/hover-click-stay';
import { useDealContext } from '@/contexts/deal-provider';
import { useDealByShortId } from '@/hooks/use-deals-selectors';
import useRedirectToInformativeDealUrl from '@/hooks/use-redirect-to-informative-deal-url';
import { pdkAxios } from '@/lib/pdk-axios';
import type { PrimaryCustomer } from '@/models/registry';
import { dealStore } from '@/models/stores/deals';
import { ConfirmationField, ConfirmationTargetType } from '@/models/types.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import InlineTextInput from '../../ui/inline-text-input';
import CoRegistrantCreateButton from '../co-registrant/co-registrant-create-button';

const PrimaryCustomerNameEditor = observer(() => {
  const { currentShortId } = useDealContext();
  const deal = useDealByShortId(currentShortId);
  const { redirectToInformativeDealUrl } = useRedirectToInformativeDealUrl();
  const dealShortId = deal?.short_id || '';
  const fullName = deal?.primary_customer?.full_name || '';
  const customerId = deal?.primary_customer?.id || '';
  const dealId = deal?.id || '';

  const [fullNameValue, setFullNameValue] = useState(fullName);
  const [isEditing, setIsEditing] = useState(false);

  const queryClient = useQueryClient();
  const updatePrimaryCustomerNameMutation = useMutation({
    onMutate: async ({ primaryCustomerName }) => {
      dealStore.updatePrimaryCustomerFullName(customerId, primaryCustomerName);
    },
    mutationFn: async ({
      primaryCustomerName,
    }: {
      primaryCustomerName: PrimaryCustomer['full_name'];
    }) => {
      if (!primaryCustomerName) return;

      const response = await pdkAxios
        .post(`/deals/${dealId}/primary-customer/${customerId}/name`, {
          full_name: primaryCustomerName,
        })
        .catch((e) => {
          console.error('Error updating primary customer name', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {},
    onError: (error) => {
      console.error('Error updating primary customer name', error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] });
    },
  });

  const handlePrimaryCustomerNameChange = useCallback(
    debounce((newPrimaryCustomerName: string) => {
      updatePrimaryCustomerNameMutation.mutate({
        primaryCustomerName: newPrimaryCustomerName,
      });
    }, 1000),
    [],
  );

  useEffect(() => {
    return () => {
      handlePrimaryCustomerNameChange.cancel();
    };
  }, []);

  useEffect(() => {
    if (isEditing) return;
    setFullNameValue(fullName);
  }, [fullName, isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    if (fullNameValue === fullName) return;
    handlePrimaryCustomerNameChange(fullNameValue);
  }, [fullNameValue, fullName, isEditing]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFullNameValue(e.target.value);
  }, []);

  return (
    <div className="flex flex-col gap-2 w-full">
      <HoverClickStay
        key="customer-hover"
        tether={
          !deal?.co_registrant && (
            <CoRegistrantCreateButton dealId={dealId} customerId={customerId} />
          )
        }
      >
        <InlineTextInput
          key={`primary-customer-name-editor-${dealShortId}-title`}
          placeholder="Customer Name"
          value={fullNameValue}
          wrapperClassName="w-full"
          className="w-full h-fit text-black py-0 text-2xl!  tracking-tight"
          readOnly
        />
      </HoverClickStay>
      <div className="flex flex-row">
        <InlineTextInput
          key={`primary-customer-name-editor-${dealShortId}-input`}
          placeholder="Customer Name"
          value={fullNameValue}
          wrapperClassName="w-full"
          className="w-full!"
          onFocus={() => setIsEditing(true)}
          onChange={onChange}
          onBlur={() => {
            setIsEditing(false);
            handlePrimaryCustomerNameChange.flush();

            if (!deal || !deal.vehicle_listing) return;

            redirectToInformativeDealUrl({
              primaryCustomerFullName: fullNameValue,
              vehicle: deal.vehicle_listing,
              primarySalesRep: deal.primary_sales_rep,
              secondarySalesRep: deal.secondary_sales_rep,
              shortId: deal.short_id,
            });
          }}
        />
        <ConfirmationDot
          targetId={dealId}
          targetType={ConfirmationTargetType.Deal}
          field={ConfirmationField.CustomerName}
          value={observable.box(fullNameValue)}
        />
      </div>
    </div>
  );
});

export default PrimaryCustomerNameEditor;
